"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, CircleQuestionMark as HelpCircle, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "What is the core difference between term and permanent life insurance?",
  answer: <div className="space-y-3">
        <p>
          Term life insurance provides coverage for a specific period, such as 10, 20, or 30 years. If the insured person dies while the policy is in force, the policy pays the death benefit according to the contract.
        </p>
        <p>
          Permanent life insurance is designed to provide longer-term or lifetime coverage and may build cash value. Whole life and universal life are examples. Premiums, cash-value growth, guarantees, and other features vary by policy and insurance carrier. Coverage remains in force only when the requirements of the policy are met.
        </p>
      </div>
}, {
  id: "item-2",
  question: "How does cash value accumulation work in permanent policies?",
  answer: <div className="space-y-3">
        <p>
          Some permanent life insurance policies build cash value over time. How that value grows depends on the type of policy and its contract terms. Whole life, universal life, and indexed universal life policies can use different methods for determining cash value and interest or crediting.
        </p>
        <p>
          Policyowners may be able to access available cash value through withdrawals or policy loans. Loans and withdrawals can reduce the policy's cash value and death benefit and may have tax consequences in some circumstances.
        </p>
      </div>
}, {
  id: "item-3",
  question: "Can I convert a term policy into permanent coverage later?",
  answer: "Some term life insurance policies include a conversion privilege that may allow eligible term coverage to be converted to permanent insurance without new medical underwriting. Conversion availability, deadlines, eligible permanent products, and other requirements depend on the specific policy and insurance carrier."
}, {
  id: "item-4",
  question: "What should I expect during the underwriting process?",
  answer: "Life insurance underwriting is the process an insurance company uses to evaluate an application and determine eligibility, coverage terms, and premium rates. Depending on the insurer, policy, coverage amount, and applicant, the process may include health questions, prescription or medical-record reviews, financial information, and sometimes a medical exam or laboratory testing. Some applicants may qualify for accelerated or simplified underwriting."
}, {
  id: "item-5",
  question: "How much life insurance coverage do I actually need?",
  answer: <div className="space-y-3">
        <p>
          There is no single coverage amount that is appropriate for everyone. Factors to consider can include income replacement, debts and mortgage obligations, dependents, education expenses, final expenses, existing insurance and assets, future financial responsibilities, goals, and budget.
        </p>
        <p>
          A licensed insurance professional can help you review these factors and compare available insurance options, but the amount of coverage you choose should reflect your individual needs and circumstances.
        </p>
      </div>
}];
export default function LifeInsurance() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="life-insurance" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: shouldReduceMotion ? 0.01 : 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="bg-muted/80 text-foreground border-border px-3.5 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
              <ShieldCheck className="size-3.5 text-foreground" />
              Life Insurance Guidance
            </Badge>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground mb-4">
            Clear answers to everyday life insurance questions
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed max-w-2xl mx-auto">
            Understand how term, permanent, and cash value policies work so you can choose coverage that fits your household.
          </p>

          <div className="flex items-center justify-center gap-2 mt-6">
            <span className="bg-primary w-1.5 h-1.5 rounded-full" />
            <span className="w-10 h-px bg-border" />
            <span className="bg-primary w-1.5 h-1.5 rounded-full" />
            <span className="w-10 h-px bg-border" />
            <span className="bg-primary w-1.5 h-1.5 rounded-full" />
            <span className="w-10 h-px bg-border" />
            <span className="bg-primary w-1.5 h-1.5 rounded-full" />
          </div>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="mx-auto max-w-3xl">
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-border/80">
                <AccordionItem value={item.id} className="border-none px-6 py-1">
                  <AccordionTrigger className="text-base sm:text-lg font-semibold text-card-foreground text-left py-5 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    <span className="pr-4">{item.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-0 pb-6 pr-2">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>

          <motion.div variants={itemVariants} className="mt-12 rounded-xl bg-card border border-border p-6 sm:p-8 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xs">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex size-10 rounded-full bg-muted items-center justify-center shrink-0 mt-0.5">
                <HelpCircle className="size-5 text-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-card-foreground">
                  Have a specific question about your situation?
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Speak with an independent consultant to review policy types, quotes, or existing coverage.
                </p>
              </div>
            </div>

            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 font-medium px-5">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                Ask a Question
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}